import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import {
  ReactFlow,
  Background,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  Handle,
  MarkerType,
  Panel,
  Position,
  ReactFlowProvider,
  getBezierPath,
  useReactFlow,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
  type OnNodesChange,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import dagre from "dagre";
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { purple, red } from "@mui/material/colors";
import {
  ArrowDownLeft,
  ArrowRight,
  Circle,
  CircleStop,
  Flag,
  Users,
  X,
  Zap,
} from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { useAuth } from "../../context/AuthContext";
import type {
  ResponsibleRole,
  WorkflowAction,
  WorkflowConfig,
  WorkflowState,
} from "../../api/workflow";
import { triggerLabel } from "./onEnter";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ESS_WORKFLOW_NAME = "ess_suggestion";
const NODE_WIDTH = 300;
// Title area is pinned to 64px (`NODE_TITLE_HEIGHT`); the footer
// adds ~32px (padding + text + top divider). Keep NODE_HEIGHT in
// sync as the dagre layout height so node bounding boxes used for
// edge routing reflect the real footprint.
const NODE_TITLE_HEIGHT = 64;
const NODE_FOOTER_HEIGHT = 32;
const NODE_HEIGHT = NODE_TITLE_HEIGHT + NODE_FOOTER_HEIGHT;

// Domain acronyms preserved verbatim when humanising snake_case
// identifiers or running Title Case on copy from the API.
const DOMAIN_ACRONYMS = new Set([
  "ESS",
  "HR",
  "WC",
  "SC",
  "HOD",
  "NM",
  "UC",
  "UA",
  "UCUA",
  "PDF",
  "ID",
  "HSEF",
  "PP",
  "PE",
]);

// Per-workflow display label override (verbose internal name → short
// readable surface). Mirrors the nmms canvas.
const WORKFLOW_DISPLAY_LABELS: Record<string, string> = {
  ess_suggestion: "ESS Suggestion",
};

function workflowDisplayLabel(name: string): string {
  return WORKFLOW_DISPLAY_LABELS[name] || humanizeIdentifier(name);
}

// ---------------------------------------------------------------------------
// Synthetic state injection: split `submitted` into "Submitted" entry
// card + "HR Review" actions card. Mirrors the nmms behaviour so the
// canvas reads as: Submitted → HR Review → Accepted by HR & forward to
// WC → … (with HR Review carrying all the review-stage actions in a
// curated order: Edit → Assign / Remove / Reassign Reviewer →
// Accept / Reject).
// ---------------------------------------------------------------------------

const HR_REVIEW_NODE_ID = `${ESS_WORKFLOW_NAME}__hr_review`;
const SUBMITTED_NODE_ID = `${ESS_WORKFLOW_NAME}__submitted`;
const HR_REVIEW_ACTION_ORDER: string[] = [
  "edit_suggestion",
  "assign_reviewers",
  "unassign_reviewer",
  "reassign_reviewer",
  "accept_hr",
  "reject_hr",
];

// ---------------------------------------------------------------------------
// Humanizers
// ---------------------------------------------------------------------------

function humanizeIdentifier(name: string | undefined | null): string {
  if (!name) return "";
  return name
    .split("_")
    .filter(Boolean)
    .map((w) => {
      const upper = w.toUpperCase();
      if (DOMAIN_ACRONYMS.has(upper)) return upper;
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    })
    .join(" ");
}

function titleCasePhrase(phrase: string | undefined | null): string {
  if (!phrase) return "";
  return phrase.replace(/[A-Za-z]+/g, (word) => {
    const upper = word.toUpperCase();
    if (DOMAIN_ACRONYMS.has(upper)) return upper;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
}

function humanizeActionLabel(action: WorkflowAction | undefined): string {
  if (!action) return "";
  if (action.button_label) return titleCasePhrase(action.button_label);
  if (action.version_history_label)
    return titleCasePhrase(action.version_history_label);
  return humanizeIdentifier(action.name || "");
}

function humanizeStateName(
  workflow: WorkflowConfig | undefined,
  stateName: string,
): string {
  const s = workflow?.states?.[stateName];
  if (s) return s.timeline_label || s.label || humanizeIdentifier(stateName);
  // Synthetic HR Review state.
  if (stateName === "hr_review") return "HR Review";
  return humanizeIdentifier(stateName);
}

// Strip implementation-detail tails (filenames / key=value pairs) from
// action descriptions. Useful for engineers but noise on a read-only
// workflow reference.
function sanitizeActionDescription(
  desc: string | undefined,
): string | undefined {
  if (!desc) return desc;
  return desc.replace(/\s*\([^)]*(?:\.html|=)[^)]*\)\s*$/i, "").trim();
}

// ---------------------------------------------------------------------------
// Enriched action / trigger types (extend the API shape with humanised
// strings the components consume directly).
// ---------------------------------------------------------------------------

interface EnrichedAction extends WorkflowAction {
  humanizedName: string;
  nextStateLabel: string | null;
}

interface EnrichedTrigger {
  raw: string;
  label: string;
}

interface StateNodeData {
  state: WorkflowState | SyntheticStateShape;
  stateName: string;
  qualifier: string | null;
  workflowName: string;
  workflowLabel: string;
  workflowAccent: string | null;
  actions: EnrichedAction[];
  onEnter: EnrichedTrigger[];
  responsibleRoles: ResponsibleRole[];
  isEntry?: boolean;
  isEnd?: boolean;
  isSelected?: boolean;
  isDraggable?: boolean;
  onSelect?: (id: string) => void;
  [key: string]: unknown;
}

// Minimal shape used by the synthetic HR Review state. Matches the
// subset of `WorkflowState` that StateNode / panel actually read.
interface SyntheticStateShape {
  label: string;
  timeline_label?: string;
  actions?: WorkflowAction[];
}

interface LabeledEdgeData {
  siblingIndex?: number;
  siblingCount?: number;
  labelT?: number;
  [key: string]: unknown;
}

type StateFlowNode = Node<StateNodeData, "state">;
type LabeledEdgeType = Edge<LabeledEdgeData, "labeled">;

// ---------------------------------------------------------------------------
// Slim card node
// ---------------------------------------------------------------------------

function StateNode({ data, id }: NodeProps<StateFlowNode>) {
  const theme = useTheme();
  const {
    state,
    qualifier,
    workflowAccent,
    actions = [],
    onEnter = [],
    responsibleRoles = [],
    isEntry,
    isEnd,
    isSelected,
    isDraggable,
    onSelect,
  } = data;
  if (!state) return null;
  const label = state.label || "";
  const titleText = qualifier ? `${label} · ${qualifier}` : label;
  const titleId = `state-node-title-${id}`;
  const actionCount = actions.length;
  const autoCount = onEnter.length;
  const roleCount = responsibleRoles.length;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect?.(id);
    }
  };

  return (
    <Box sx={{ position: "relative", width: NODE_WIDTH }}>
      <Handle
        type="target"
        position={Position.Left}
        style={{ background: "transparent", border: "none" }}
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Right}
        style={{ background: "transparent", border: "none" }}
        isConnectable={false}
      />
      {isEntry && (
        <Box
          aria-label="Workflow start"
          sx={{
            position: "absolute",
            top: 16,
            right: "calc(100% + 8px)",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderRadius: 999,
            bgcolor: "success.main",
            color: "common.white",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            boxShadow: 1,
            whiteSpace: "nowrap",
            zIndex: 1,
          }}>
          <RavenIcon icon={Flag} sx={{ fontSize: 14 }} aria-hidden="true" />
          Start
        </Box>
      )}
      {isEnd && (
        <Box
          aria-label="Workflow end"
          sx={{
            position: "absolute",
            top: 16,
            left: "calc(100% + 8px)",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderRadius: 999,
            bgcolor: "grey.600",
            color: "common.white",
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            boxShadow: 1,
            whiteSpace: "nowrap",
            zIndex: 1,
          }}>
          <RavenIcon
            icon={CircleStop}
            sx={{ fontSize: 14 }}
            aria-hidden="true"
          />
          End
        </Box>
      )}
      <Box
        role="button"
        tabIndex={0}
        aria-labelledby={titleId}
        aria-pressed={isSelected ? "true" : "false"}
        onKeyDown={handleKeyDown}
        sx={{
          width: NODE_WIDTH,
          height: NODE_HEIGHT,
          background: theme.palette.background.paper,
          border: "1px solid",
          borderColor: isSelected
            ? "primary.main"
            : isDraggable
              ? "warning.main"
              : "divider",
          borderStyle: isDraggable ? "dashed" : "solid",
          borderRadius: "16px",
          boxShadow: theme.shadows[1],
          overflow: "hidden",
          cursor: isDraggable ? "grab" : "pointer",
          userSelect: "none",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          zIndex: 2,
          transition: "border-color 120ms ease, box-shadow 120ms ease",
          "&:hover": {
            borderColor: "primary.main",
            boxShadow: theme.shadows[4],
          },
          "&:active": { cursor: isDraggable ? "grabbing" : "pointer" },
          ...(workflowAccent && {
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: workflowAccent,
              zIndex: 1,
              pointerEvents: "none",
            },
          }),
          ...(isSelected && {
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}, ${theme.shadows[4]}`,
          }),
        }}>
        <Box
          sx={{
            height: NODE_TITLE_HEIGHT,
            flexShrink: 0,
            padding: theme.spacing(0, 2),
            background: theme.palette.background.default,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: 0,
          }}>
          <Typography
            id={titleId}
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "text.primary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.3,
            }}>
            {titleText}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            padding: theme.spacing(0.75, 2),
            borderTop: "1px solid",
            borderColor: "divider",
            fontSize: 11,
            fontWeight: 500,
            color: "text.secondary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
          {actionCount > 0 ? (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                "& svg": { fontSize: 13 },
              }}>
              <RavenIcon
                icon={Circle}
                sx={{ fontSize: 8, color: "primary.main", fill: "currentColor" }}
                aria-hidden="true"
              />
              {actionCount} {actionCount === 1 ? "action" : "actions"}
            </Box>
          ) : isEntry ? null : (
            <Box component="span" sx={{ fontStyle: "italic" }}>
              Terminal
            </Box>
          )}
          {autoCount > 0 && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                "& svg": { fontSize: 13 },
              }}>
              <RavenIcon icon={Zap} aria-hidden="true" />
              {autoCount} auto
            </Box>
          )}
          {roleCount > 0 && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                "& svg": { fontSize: 13 },
              }}>
              <RavenIcon
                icon={Users}
                sx={{ color: "primary.main" }}
                aria-hidden="true"
              />
              {roleCount} {roleCount === 1 ? "role" : "roles"}
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          <Box component="span" sx={{ fontSize: 11, opacity: 0.7 }}>
            View details →
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const nodeTypes = { state: StateNode };

// ---------------------------------------------------------------------------
// Custom bezier edge with portaled label
// ---------------------------------------------------------------------------

function LabeledEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  label,
  style,
  markerEnd,
  data,
}: EdgeProps<LabeledEdgeType>) {
  const theme = useTheme();
  const siblingIndex = data?.siblingIndex ?? 0;
  const siblingCount = data?.siblingCount ?? 1;
  const curvature =
    siblingCount > 1
      ? 0.25 + (siblingIndex - (siblingCount - 1) / 2) * 0.15
      : 0.25;
  const effectiveCurvature = Math.max(0.05, curvature);
  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    curvature: effectiveCurvature,
  });

  // Anchor each label at a stable per-edge t value. Siblings span
  // [0.3, 0.7] evenly so they don't pile up; single edges use a hash
  // of the edge id to scatter them across mid-curve. The post-render
  // collision pass below can override via data.labelT.
  let labelT: number;
  if (data?.labelT != null) {
    labelT = data.labelT;
  } else if (siblingCount > 1) {
    labelT = 0.3 + (0.4 * siblingIndex) / (siblingCount - 1);
  } else {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
    labelT = 0.35 + ((Math.abs(h) % 100) / 100) * 0.3;
  }

  // Mirror React Flow's `calculateControlOffset` from getBezierPath so
  // our label-position math uses the SAME control points the SVG path
  // is drawn with.
  const controlOffset = (distance: number): number =>
    distance >= 0
      ? 0.5 * distance
      : effectiveCurvature * 25 * Math.sqrt(-distance);
  const dx = targetX - sourceX;
  const cp1x = sourceX + controlOffset(dx);
  const cp1y = sourceY;
  const cp2x = targetX - controlOffset(dx);
  const cp2y = targetY;
  const bezierPoint = (
    t: number,
    p0: number,
    p1: number,
    p2: number,
    p3: number,
  ): number => {
    const u = 1 - t;
    return (
      u * u * u * p0 +
      3 * u * u * t * p1 +
      3 * u * t * t * p2 +
      t * t * t * p3
    );
  };
  const labelX = bezierPoint(labelT, sourceX, cp1x, cp2x, targetX);
  const labelY = bezierPoint(labelT, sourceY, cp1y, cp2y, targetY);

  return (
    <>
      <BaseEdge id={id} path={edgePath} style={style} markerEnd={markerEnd} />
      {label && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan"
            data-edge-id={id}
            style={{
              position: "absolute",
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: "all",
              // Opaque background + solid border + own stacking
              // context so any unrelated curve passing beneath the
              // label box is definitively hidden — the border + drop
              // shadow give the label a hard visual edge so the line
              // appears to *stop* at the rim rather than bleeding
              // under it.
              background: theme.palette.background.paper,
              padding: "5px 12px",
              borderRadius: 8,
              border: `1px solid ${theme.palette.divider}`,
              fontSize: 12,
              fontWeight: 600,
              color: theme.palette.text.primary,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 2px 6px rgba(0, 0, 0, 0.5)"
                  : "0 2px 6px rgba(0, 0, 0, 0.12)",
              whiteSpace: "nowrap",
              zIndex: 5,
            }}>
            {label as string}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const edgeTypes = { labeled: LabeledEdge };

// ---------------------------------------------------------------------------
// Side details panel
// ---------------------------------------------------------------------------

interface StateDetailsPanelProps {
  node: StateFlowNode | null;
  allNodes: StateFlowNode[];
  allEdges: LabeledEdgeType[];
  onClose: () => void;
}

function StateDetailsPanel({
  node,
  allNodes,
  allEdges,
  onClose,
}: StateDetailsPanelProps) {
  const theme = useTheme();
  const nodesById = useMemo(() => {
    const m = new Map<string, StateFlowNode>();
    allNodes.forEach((n) => m.set(n.id, n));
    return m;
  }, [allNodes]);
  const incoming = useMemo(() => {
    if (!node) return [];
    return allEdges
      .filter((e) => e.target === node.id && e.source !== node.id)
      .map((e) => {
        const src = nodesById.get(e.source);
        const srcLabel = src
          ? src.data.state.label || src.data.stateName
          : e.source;
        const srcWorkflow = src?.data.workflowLabel || "";
        return {
          id: e.id,
          label: (e.label as string) || "Automatic handoff",
          srcLabel,
          srcWorkflow,
        };
      });
  }, [node, allEdges, nodesById]);

  useEffect(() => {
    if (!node) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [node, onClose]);

  if (!node) return null;
  const {
    state,
    qualifier,
    workflowLabel,
    workflowName,
    workflowAccent,
    actions = [],
    onEnter = [],
    responsibleRoles = [],
    stateName,
  } = node.data;
  const label = state.label || "";
  const titleText = qualifier ? `${label} · ${qualifier}` : label;

  const sectionLabelSx = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase" as const,
    color: "text.secondary",
    mb: 1,
  };
  const overlayBg =
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.06)"
      : "rgba(0,0,0,0.03)";

  return (
    <Box
      role="dialog"
      aria-modal="false"
      aria-labelledby="state-details-title"
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: 380,
        maxWidth: "min(380px, 100vw)",
        bgcolor: "background.paper",
        borderLeft: "1px solid",
        borderColor: "divider",
        zIndex: 10,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}>
      {workflowAccent && (
        <Box
          sx={{ height: 4, background: workflowAccent, flexShrink: 0 }}
        />
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.4px",
              textTransform: "uppercase",
              color: "text.secondary",
            }}>
            {workflowLabel || workflowName}
          </Typography>
          <Typography
            id="state-details-title"
            sx={{
              fontSize: 18,
              fontWeight: 700,
              color: "text.primary",
              wordBreak: "break-word",
              lineHeight: 1.3,
            }}>
            {titleText}
          </Typography>
        </Box>
        <Box
          component="button"
          onClick={onClose}
          aria-label="Close details panel"
          sx={{
            appearance: "none",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            color: "text.secondary",
            p: 0.5,
            borderRadius: 1,
            display: "inline-flex",
            "&:hover": { bgcolor: overlayBg, color: "text.primary" },
          }}>
          <RavenIcon icon={X} />
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        {actions.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={sectionLabelSx}>Actions</Typography>
            <Stack spacing={1}>
              {actions.map((action) => {
                const stays =
                  !action.next_state || action.next_state === stateName;
                return (
                  <Box
                    key={action.name}
                    sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      borderLeft: "3px solid",
                      borderLeftColor: "primary.light",
                      bgcolor: overlayBg,
                    }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                      }}>
                      <Box
                        sx={{
                          width: 14,
                          height: 18,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          mr: "6px",
                        }}
                        aria-hidden="true">
                        <RavenIcon
                          icon={Circle}
                          sx={{
                            fontSize: 8,
                            color: "primary.main",
                            fill: "currentColor",
                            mt: "1px",
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            flexWrap: "wrap",
                          }}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "text.primary",
                            }}>
                            {action.humanizedName}
                          </Typography>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.25,
                            }}>
                            <RavenIcon
                              icon={ArrowRight}
                              sx={{ fontSize: 14, color: "text.disabled" }}
                              aria-hidden="true"
                            />
                            <Typography
                              sx={{
                                fontSize: 13,
                                fontWeight: 500,
                                color: "text.secondary",
                                fontStyle: stays ? "italic" : "normal",
                              }}>
                              {stays ? "stays here" : action.nextStateLabel}
                            </Typography>
                          </Box>
                        </Box>
                        {action.description && (
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 400,
                              color: "text.primary",
                              mt: 0.5,
                              wordBreak: "break-word",
                            }}>
                            {action.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}
        {responsibleRoles.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={sectionLabelSx}>Responsible roles</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {responsibleRoles.map((r) => (
                <Box
                  key={r.role}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 999,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: overlayBg,
                    fontSize: 12,
                    fontWeight: 500,
                    color: "text.primary",
                  }}>
                  <RavenIcon
                    icon={Users}
                    sx={{ fontSize: 14, color: "primary.main" }}
                    aria-hidden="true"
                  />
                  {r.label}
                </Box>
              ))}
            </Box>
          </Box>
        )}
        {onEnter.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={sectionLabelSx}>Happens automatically</Typography>
            <Stack spacing={0.75}>
              {onEnter.map((trigger) => (
                <Box
                  key={trigger.raw}
                  sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                  <RavenIcon
                    icon={Zap}
                    sx={{
                      fontSize: 14,
                      color: "warning.main",
                      mt: "2px",
                      flexShrink: 0,
                    }}
                    aria-hidden="true"
                  />
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 400,
                      color: "text.primary",
                    }}>
                    {trigger.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
        {incoming.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={sectionLabelSx}>Incoming</Typography>
            <Stack spacing={0.75}>
              {incoming.map((inc) => (
                <Box
                  key={inc.id}
                  sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                  <RavenIcon
                    icon={ArrowDownLeft}
                    sx={{
                      fontSize: 14,
                      color: "text.secondary",
                      mt: "2px",
                      flexShrink: 0,
                    }}
                    aria-hidden="true"
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "text.primary",
                      }}>
                      {inc.label}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 11, color: "text.secondary" }}>
                      from {inc.srcLabel}
                      {inc.srcWorkflow ? ` · ${inc.srcWorkflow}` : ""}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
        {actions.length === 0 &&
          onEnter.length === 0 &&
          responsibleRoles.length === 0 &&
          incoming.length === 0 && (
            <Typography variant="body2" color="text.disabled">
              Terminal state — no further actions.
            </Typography>
          )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Graph build (ESS only, with HR Review synthesis)
// ---------------------------------------------------------------------------

function buildGraph(
  workflow: WorkflowConfig,
  prefersReducedMotion: boolean,
): { nodes: StateFlowNode[]; edges: LabeledEdgeType[] } {
  const nodes: StateFlowNode[] = [];
  const edges: LabeledEdgeType[] = [];
  const nodeId = (stateName: string) => `${workflow.name}__${stateName}`;
  const targetNodeIds = new Set<string>();
  const workflowLabel = workflowDisplayLabel(workflow.name);

  Object.entries(workflow.states).forEach(([stateName, state]) => {
    const enrichedActions: EnrichedAction[] = (state.actions || []).map(
      (action) => ({
        ...action,
        humanizedName: humanizeActionLabel(action),
        nextStateLabel: action.next_state
          ? humanizeStateName(workflow, action.next_state)
          : null,
        description: sanitizeActionDescription(action.description),
      }),
    );

    const enrichedOnEnter: EnrichedTrigger[] = (state.on_enter || []).map(
      (raw) => ({ raw, label: triggerLabel(raw, workflow.callback_labels) }),
    );

    const isEssSubmitted =
      workflow.name === ESS_WORKFLOW_NAME && stateName === "submitted";

    nodes.push({
      id: nodeId(stateName),
      type: "state",
      position: { x: 0, y: 0 },
      data: {
        state,
        stateName,
        qualifier: null,
        workflowName: workflow.name,
        workflowLabel,
        workflowAccent: null,
        actions: isEssSubmitted ? [] : enrichedActions,
        // The `submitted` state's on_enter alerts fire on submission, so
        // they belong to the "Submitted" entry card — not the synthetic
        // "HR Review" card (which represents HR's action window, below).
        onEnter: enrichedOnEnter,
        // Responsible roles describe who *acts* at this state, so — like the
        // actions — they ride on the HR Review card for ESS `submitted`, not
        // the bare entry card.
        responsibleRoles: isEssSubmitted ? [] : state.responsible_roles || [],
      },
    });

    if (isEssSubmitted) {
      const orderedActions = [...enrichedActions].sort((a, b) => {
        const ai = HR_REVIEW_ACTION_ORDER.indexOf(a.name);
        const bi = HR_REVIEW_ACTION_ORDER.indexOf(b.name);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
      nodes.push({
        id: HR_REVIEW_NODE_ID,
        type: "state",
        position: { x: 0, y: 0 },
        data: {
          state: { label: "HR Review", timeline_label: "HR Review" },
          stateName: "hr_review",
          qualifier: null,
          workflowName: workflow.name,
          workflowLabel,
          workflowAccent: null,
          actions: orderedActions,
          onEnter: [],
          responsibleRoles: state.responsible_roles || [],
        },
      });
      // Auto-routes Submitted → HR Review.
      targetNodeIds.add(HR_REVIEW_NODE_ID);
      edges.push({
        id: `${SUBMITTED_NODE_ID}=>${HR_REVIEW_NODE_ID}`,
        source: SUBMITTED_NODE_ID,
        target: HR_REVIEW_NODE_ID,
        label: "Auto-routes to HR",
        type: "labeled",
        animated: !prefersReducedMotion,
        markerEnd: { type: MarkerType.ArrowClosed, color: purple[200] },
        style: {
          stroke: purple[200],
          strokeWidth: 1.5,
          strokeDasharray: "5 5",
        },
      });
    }

    const actionEdgeSource = isEssSubmitted
      ? HR_REVIEW_NODE_ID
      : nodeId(stateName);
    const selfLoopState = isEssSubmitted ? "submitted" : stateName;
    (state.actions || []).forEach((action) => {
      if (!action.next_state) return;
      if (action.next_state === selfLoopState) return;
      const target = nodeId(action.next_state);
      targetNodeIds.add(target);
      const accent = edgeAccentForAction(action, action.next_state);
      edges.push({
        id: `${actionEdgeSource}->${target}__${action.name}`,
        source: actionEdgeSource,
        target,
        label: humanizeActionLabel(action),
        type: "labeled",
        animated: !prefersReducedMotion,
        markerEnd: { type: MarkerType.ArrowClosed, color: accent },
        style: { stroke: accent, strokeWidth: 1.5, strokeDasharray: "5 5" },
      });
    });
  });

  // Sibling annotations for label spread.
  const edgesBySource = new Map<string, LabeledEdgeType[]>();
  edges.forEach((e) => {
    if (!edgesBySource.has(e.source)) edgesBySource.set(e.source, []);
    edgesBySource.get(e.source)!.push(e);
  });
  edgesBySource.forEach((group) => {
    const count = group.length;
    group.forEach((e, i) => {
      e.data = { ...(e.data || {}), siblingIndex: i, siblingCount: count };
    });
  });

  // Entry flag — the workflow's initial_state unless something points
  // at it. In the ESS-only canvas with synthesis, the entry is
  // `submitted`.
  if (workflow.initial_state) {
    const entryId = nodeId(workflow.initial_state);
    if (!targetNodeIds.has(entryId)) {
      const entry = nodes.find((n) => n.id === entryId);
      if (entry) entry.data.isEntry = true;
    }
  }

  // End flag — terminal happy-path node. For ESS that's
  // implementation_closed.
  const sourceNodeIds = new Set(edges.map((e) => e.source));
  const happyPath = workflow.happy_path || [];
  const happyEnd = happyPath[happyPath.length - 1];
  if (happyEnd) {
    const endId = nodeId(happyEnd);
    if (!sourceNodeIds.has(endId)) {
      const endNode = nodes.find((n) => n.id === endId);
      if (endNode) endNode.data.isEnd = true;
    }
  }

  return { nodes, edges };
}

function edgeAccentForAction(
  action: WorkflowAction,
  targetStateName: string,
): string {
  const name = action.name?.toLowerCase() || "";
  const target = targetStateName.toLowerCase();
  const isReject =
    name.startsWith("reject") ||
    name.startsWith("delete") ||
    /(^|_)reject(ed)?(_|$)/.test(target) ||
    target === "deleted";
  return isReject ? red[300] : purple[200];
}

// ---------------------------------------------------------------------------
// Dagre layout
// ---------------------------------------------------------------------------

function layoutGraph(
  nodes: StateFlowNode[],
  edges: LabeledEdgeType[],
): StateFlowNode[] {
  const g = new dagre.graphlib.Graph({ compound: false });
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: "LR",
    nodesep: 320,
    ranksep: 440,
    marginx: 32,
    marginy: 32,
    edgesep: 80,
  });
  nodes.forEach((n) =>
    g.setNode(n.id, { width: NODE_WIDTH, height: NODE_HEIGHT }),
  );
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);
  return nodes.map((n) => {
    const { x, y, width, height } = g.node(n.id);
    return {
      ...n,
      position: { x: x - width / 2, y: y - height / 2 },
      width,
      height,
      style: { width: NODE_WIDTH } as CSSProperties,
    };
  });
}

// ---------------------------------------------------------------------------
// Canvas (inner — needs ReactFlowProvider)
// ---------------------------------------------------------------------------

interface WorkflowCanvasInnerProps {
  workflow: WorkflowConfig;
  height: string | number;
}

function WorkflowCanvasInner({ workflow, height }: WorkflowCanvasInnerProps) {
  const theme = useTheme();
  const { isAdmin } = useAuth();
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );
  const storageKey = useMemo(
    () => `ess-wf-layout-${workflow.name}`,
    [workflow.name],
  );
  const [positionOverrides, setPositionOverrides] = useState<
    Record<string, { x: number; y: number }>
  >(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const { nodes, edges } = useMemo(() => {
    const graph = buildGraph(workflow, prefersReducedMotion);
    const laidOutNodes = layoutGraph(graph.nodes, graph.edges);
    const overriddenNodes = laidOutNodes.map((n) =>
      positionOverrides[n.id]
        ? { ...n, position: positionOverrides[n.id] }
        : n,
    );
    return { nodes: overriddenNodes, edges: graph.edges };
  }, [workflow, prefersReducedMotion, positionOverrides]);

  const [labelTOverrides, setLabelTOverrides] = useState<Record<string, number>>(
    {},
  );
  const edgesWithLabelOverrides = useMemo(
    () =>
      edges.map((e) => {
        const t = labelTOverrides[e.id];
        if (t == null) return e;
        return { ...e, data: { ...(e.data || {}), labelT: t } };
      }),
    [edges, labelTOverrides],
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const handleSelect = useCallback((id: string) => {
    setSelectedId((prev) => (prev === id ? null : id));
  }, []);
  const closePanel = useCallback(() => setSelectedId(null), []);

  const [isEditMode, setIsEditMode] = useState(false);
  const enterEditMode = useCallback(() => {
    setIsEditMode(true);
    setSelectedId(null);
  }, []);
  const exitEditMode = useCallback(() => setIsEditMode(false), []);

  const handleNodeDragStop = useCallback(
    (_: unknown, node: Node) => {
      setPositionOverrides((prev) => {
        const next = { ...prev, [node.id]: node.position };
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          /* private mode / quota — swallow */
        }
        return next;
      });
    },
    [storageKey],
  );

  const nodesWithSelection: StateFlowNode[] = useMemo(
    () =>
      nodes.map((n) => ({
        ...n,
        draggable: isEditMode,
        data: {
          ...n.data,
          isSelected: !isEditMode && n.id === selectedId,
          isDraggable: isEditMode,
          onSelect: handleSelect,
        },
      })),
    [nodes, selectedId, handleSelect, isEditMode],
  );

  const selectedNode = useMemo(
    () => nodesWithSelection.find((n) => n.id === selectedId) || null,
    [nodesWithSelection, selectedId],
  );

  // Post-render label-collision pass — same logic as nmms.
  const collisionPassCountRef = useRef(0);
  useEffect(() => {
    collisionPassCountRef.current = 0;
  }, [nodes]);
  useEffect(() => {
    if (collisionPassCountRef.current > 10) return;
    const rafId = window.requestAnimationFrame(() => {
      const labelEls = document.querySelectorAll<HTMLDivElement>(
        ".react-flow__edgelabel-renderer > div[data-edge-id]",
      );
      if (labelEls.length === 0) return;
      const pathEls = document.querySelectorAll<SVGPathElement>(
        "path.react-flow__edge-path",
      );
      const pathById = new Map<string, SVGPathElement>();
      pathEls.forEach((p) => {
        const id = p.closest("[data-id]")?.getAttribute("data-id");
        if (id) pathById.set(id, p);
      });
      const samplePath = (p: SVGPathElement, count = 32) => {
        const len = p.getTotalLength();
        if (!len) return [];
        const m = p.getScreenCTM();
        if (!m) return [];
        const out: { x: number; y: number }[] = [];
        for (let i = 0; i <= count; i++) {
          const pt = p.getPointAtLength((len * i) / count);
          out.push({
            x: pt.x * m.a + pt.y * m.c + m.e,
            y: pt.x * m.b + pt.y * m.d + m.f,
          });
        }
        return out;
      };
      const samplesById = new Map<string, { x: number; y: number }[]>();
      pathById.forEach((p, id) => samplesById.set(id, samplePath(p)));

      // Snapshot every label's current bounding box so we can detect
      // label-vs-label overlap in the same pass.
      const labelBoxes = new Map<
        string,
        { l: number; r: number; t: number; b: number }
      >();
      labelEls.forEach((el) => {
        const id = el.getAttribute("data-edge-id");
        if (!id) return;
        const r = el.getBoundingClientRect();
        labelBoxes.set(id, { l: r.left, r: r.right, t: r.top, b: r.bottom });
      });

      const PAD = 6;
      type Box = { l: number; r: number; t: number; b: number };
      const countCrossings = (box: Box, ownerId: string) => {
        let n = 0;
        samplesById.forEach((samples, id) => {
          if (id === ownerId) return;
          for (const pt of samples) {
            if (
              pt.x > box.l &&
              pt.x < box.r &&
              pt.y > box.t &&
              pt.y < box.b
            ) {
              n++;
              break;
            }
          }
        });
        return n;
      };
      const countLabelOverlaps = (box: Box, ownerId: string) => {
        let n = 0;
        labelBoxes.forEach((lbox, id) => {
          if (id === ownerId) return;
          if (
            box.l < lbox.r &&
            box.r > lbox.l &&
            box.t < lbox.b &&
            box.b > lbox.t
          ) {
            n++;
          }
        });
        return n;
      };
      // Combined score — label-vs-label overlaps are weighted ×2
      // because they're more visually ambiguous than a label sitting
      // on a line that just passes beneath it.
      const scoreAt = (box: Box, ownerId: string) =>
        countCrossings(box, ownerId) + 2 * countLabelOverlaps(box, ownerId);

      const next: Record<string, number> = {};
      let changed = false;
      labelEls.forEach((labelEl) => {
        const edgeId = labelEl.getAttribute("data-edge-id")!;
        const r = labelEl.getBoundingClientRect();
        const currentBox = {
          l: r.left - PAD,
          r: r.right + PAD,
          t: r.top - PAD,
          b: r.bottom + PAD,
        };
        const currentScore = scoreAt(currentBox, edgeId);
        if (currentScore === 0) return;
        const ownPath = pathById.get(edgeId);
        if (!ownPath) return;
        const m = ownPath.getScreenCTM();
        if (!m) return;
        const d = ownPath.getAttribute("d") || "";
        const nums = d.match(/-?\d+(\.\d+)?/g);
        if (!nums || nums.length < 8) return;
        const [sx0, sy0, cp1x0, cp1y0, cp2x0, cp2y0, tx0, ty0] = nums
          .slice(0, 8)
          .map(Number);
        const bezier = (
          t: number,
          p0: number,
          p1: number,
          p2: number,
          p3: number,
        ) => {
          const u = 1 - t;
          return (
            u * u * u * p0 +
            3 * u * u * t * p1 +
            3 * u * t * t * p2 +
            t * t * t * p3
          );
        };
        const w = r.width;
        const h = r.height;
        const candidates = [
          0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.45, 0.5,
          0.55, 0.6, 0.65, 0.7, 0.75, 0.8, 0.85, 0.9,
        ];
        let bestT: number | null = null;
        let bestScore = currentScore;
        for (const t of candidates) {
          const localX = bezier(t, sx0, cp1x0, cp2x0, tx0);
          const localY = bezier(t, sy0, cp1y0, cp2y0, ty0);
          const cx = localX * m.a + localY * m.c + m.e;
          const cy = localX * m.b + localY * m.d + m.f;
          const box = {
            l: cx - w / 2 - PAD,
            r: cx + w / 2 + PAD,
            t: cy - h / 2 - PAD,
            b: cy + h / 2 + PAD,
          };
          const s = scoreAt(box, edgeId);
          if (s < bestScore) {
            bestScore = s;
            bestT = t;
          }
        }
        if (bestT !== null) {
          next[edgeId] = bestT;
          changed = true;
        }
      });
      if (changed) {
        collisionPassCountRef.current += 1;
        setLabelTOverrides((prev) => {
          const merged = { ...prev };
          let actuallyChanged = false;
          for (const [k, v] of Object.entries(next)) {
            if (Math.abs((merged[k] ?? -1) - v) > 0.001) {
              merged[k] = v;
              actuallyChanged = true;
            }
          }
          return actuallyChanged ? merged : prev;
        });
      }
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [nodes, edges, labelTOverrides]);

  // Focus-mode: center on start at 1x zoom on first render.
  const rf = useReactFlow();
  const hasFocusedOnStartRef = useRef(false);
  const startNode = useMemo(
    () => nodes.find((n) => n.data.isEntry),
    [nodes],
  );
  useEffect(() => {
    if (hasFocusedOnStartRef.current) return;
    if (!startNode) return;
    const w = startNode.width || NODE_WIDTH;
    const h = startNode.height || NODE_HEIGHT;
    rf.setCenter(
      startNode.position.x + w / 2,
      startNode.position.y + h / 2,
      { zoom: 1, duration: 0 },
    );
    hasFocusedOnStartRef.current = true;
  }, [rf, startNode]);

  // onNodesChange must be supplied for controlled drag to register;
  // we only need the change events to actually re-render — final
  // positions are captured by onNodeDragStop above.
  const handleNodesChange: OnNodesChange = useCallback(() => {
    /* no-op — drag positions are committed via onNodeDragStop */
  }, []);

  return (
    <Box
      sx={{
        height,
        width: "100%",
        position: "relative",
        overflow: "hidden",
        background:
          theme.palette.mode === "dark"
            ? theme.palette.background.default
            : (theme.palette.background.light ?? theme.palette.grey[100]),
        "& .react-flow__pane": { cursor: "grab" },
        "& .react-flow__pane:active": { cursor: "grabbing" },
        "& .react-flow__node": { zIndex: "10 !important" },
        "& .react-flow__node:focus": { outline: "none" },
        "& .react-flow__node [role='button']:focus": { outline: "none" },
        "& .react-flow__node [role='button']:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
        "& .react-flow__edge:focus": { outline: "none" },
        "& .react-flow__edge:focus-visible .react-flow__edge-path": {
          stroke: theme.palette.primary.main,
          strokeWidth: 2.5,
        },
        "& .react-flow__controls": {
          background: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: theme.shadows[2],
        },
        "& .react-flow__controls-button": {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
          "& svg": { fill: theme.palette.text.primary },
          "&:hover": {
            background:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : alpha(theme.palette.primary.main, 0.06),
          },
        },
        "& .react-flow__controls-button:last-of-type": {
          borderBottom: "none",
        },
      }}>
      <Box
        component="p"
        id="ess-workflow-canvas-help"
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}>
        Workflow flowchart. Press Tab to move focus between state cards
        and transitions. Drag to pan, scroll to zoom. This is a
        read-only reference — no actions are triggered on Enter.
      </Box>
      <ReactFlow
        aria-describedby="ess-workflow-canvas-help"
        nodes={nodesWithSelection}
        edges={edgesWithLabelOverrides}
        onNodeClick={(_, n) => {
          if (isEditMode) return;
          handleSelect(n.id);
        }}
        onNodeDragStop={handleNodeDragStop}
        onNodesChange={handleNodesChange}
        onPaneClick={closePanel}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        minZoom={0.2}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        nodesFocusable={false}
        edgesFocusable
        elementsSelectable={false}
        selectNodesOnDrag={false}
        defaultEdgeOptions={{
          type: "labeled",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}>
        <Background
          gap={22}
          size={1.6}
          color={theme.palette.text.disabled}
        />
        <Controls showInteractive={false} />
        {isAdmin && (
          <Panel position="bottom-center">
            <Button
              onClick={isEditMode ? exitEditMode : enterEditMode}
              variant={isEditMode ? "contained" : "outlined"}
              color="primary"
              size="small"
              sx={{ borderRadius: 999, px: 2 }}>
              {isEditMode ? "Save View" : "Edit View"}
            </Button>
          </Panel>
        )}
      </ReactFlow>
      <StateDetailsPanel
        node={selectedNode}
        allNodes={nodesWithSelection}
        allEdges={edges}
        onClose={closePanel}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Public entry point
// ---------------------------------------------------------------------------

interface WorkflowCanvasProps {
  workflow: WorkflowConfig;
  height?: string | number;
}

export default function WorkflowCanvas({
  workflow,
  height = "100%",
}: WorkflowCanvasProps) {
  if (!workflow) return null;
  return (
    <ReactFlowProvider>
      <WorkflowCanvasInner workflow={workflow} height={height} />
    </ReactFlowProvider>
  );
}
